import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Bugsnag from '@bugsnag/js';
import styled from 'styled-components';
import { Box, Inline, Text } from '@99designs/emu';
import { useCheckPrintSupported } from '@99designs/print';
import { Loading, useSite } from '@99designs/spa-common';
import { trackEvent } from '@99designs/tracking';
import { ObservableField, useForm } from '../../ObservableForm';
import { PrintServiceOptionsFieldFragment } from './PrintServiceOptionsField.generated';
import { PrintTemplateDownloadButton } from './PrintTemplateDownloadButton';
import { VistaprintOptions } from './VistaprintOptions';
import {
    usePrintByExternalIdQuery,
    useViewerQuery,
    useVistaprintProductQuery,
} from './VistaprintProduct.generated';

const EVENT_PRINT_SERVICE_OPTION_SELECTED = 'Project brief print service option selected';

export type CategoryFieldsFragment = {
    key: string;
    title: string;
    priceRange: Array<number>;
};

export type Props = PrintServiceOptionsFieldFragment;

export type PrintServiceOptionsEditProps = Props & {
    category?: CategoryFieldsFragment;
    fulfilmentMpvId?: string;
};

export function Edit({
    id,
    subtext,
    image,
    category: staticCategory,
    fulfilmentMpvId,
}: PrintServiceOptionsEditProps) {
    const site = useSite();
    const { locale } = site.siteConfig;
    const form = useForm();
    const { category, briefId } = form.data;
    const isCategoryPrintSupported = useCheckPrintSupported(category, locale);
    const isPrintSupported = !!fulfilmentMpvId || isCategoryPrintSupported;
    const { data, loading } = useVistaprintProductQuery({
        variables: {
            ...(fulfilmentMpvId ? { mpvId: fulfilmentMpvId } : { category }),
            locale,
            externalId: briefId,
        },
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-first',
        skip: !isPrintSupported,
        onError: (error) => {
            Bugsnag.notify(error, (event) => {
                event.context = 'print';
            });
        },
    });

    useEffect(() => {
        if (form.get('printServiceOptions') === '' && isPrintSupported) {
            form.set('printServiceOptions', 'VISTAPRINT');
            trackEvent(EVENT_PRINT_SERVICE_OPTION_SELECTED, {
                brief_id: briefId,
                service_type: category,
                print_service: 'VISTAPRINT',
            });
        } else if (isPrintSupported === false && form.get('printServiceOptions') !== '') {
            form.set('printServiceOptions', '');
            trackEvent(EVENT_PRINT_SERVICE_OPTION_SELECTED, {
                brief_id: briefId,
                service_type: category,
                print_service: '',
            });
        }
    }, [isPrintSupported, form, briefId, category]);

    if (!isPrintSupported) {
        return null;
    }

    if (loading) {
        return <Loading />;
    }

    if (!data || !data.vistaprintProduct) {
        return null;
    }

    return (
        <ObservableField
            form={form}
            name={id}
            title=""
            message=""
            render={() => (
                <VistaprintOptions
                    form={form}
                    category={fulfilmentMpvId ? staticCategory?.key : category}
                    mpvId={fulfilmentMpvId ?? undefined}
                    briefId={briefId}
                    image={image}
                    subtext={subtext ?? ''}
                    product={data.vistaprintProduct}
                    printRecord={data.printByExternalId}
                />
            )}
        />
    );
}

const InlineWrapper = styled(Inline)`
    align-items: flex-start;
`;

const TemplatePreview = styled.img`
    max-height: 175px;
`;

export type PrintServiceOptionsViewProps = Props & {
    briefId: string;
    category: CategoryFieldsFragment;
};

export function View({ value, briefId, category }: PrintServiceOptionsViewProps) {
    const { projectId } = useParams<{ projectId: string }>();
    const { data } = useViewerQuery();

    const {
        data: printData,
        loading,
        error,
    } = usePrintByExternalIdQuery({
        variables: {
            externalId: briefId,
        },
        skip: value !== 'VISTAPRINT',
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-first',
    });

    if (value !== 'VISTAPRINT') {
        return null;
    }

    if (loading || !printData) {
        return <Loading />;
    }

    if (
        !printData.printByExternalId ||
        (printData.printByExternalId && printData.printByExternalId.options.length === 0)
    ) {
        return null;
    }

    if (error) {
        throw error;
    }

    return (
        <Box marginTop="0.5bu">
            <InlineWrapper space="1.5bu">
                {!!printData?.printByExternalId.templatePreview && (
                    <TemplatePreview
                        src={printData.printByExternalId.templatePreview}
                        alt="Template preview"
                    />
                )}

                <Box>
                    {printData.printByExternalId.options.map((option) => (
                        <Text key={option.key} as="p">
                            <b>{option.keyName} </b>
                            {option.valueName}
                        </Text>
                    ))}
                    {!!data?.me.user && (
                        <Box marginTop="1bu">
                            <PrintTemplateDownloadButton
                                location="overview_page"
                                projectId={projectId}
                                category={category}
                                externalPrintId={printData.printByExternalId.id}
                            />
                        </Box>
                    )}
                </Box>
            </InlineWrapper>
        </Box>
    );
}

export function HasValue({ value, briefId }: PrintServiceOptionsViewProps): boolean {
    const {
        data: printData,
        loading,
        error,
    } = usePrintByExternalIdQuery({
        variables: {
            externalId: briefId,
        },
        skip: value !== 'VISTAPRINT',
        fetchPolicy: 'network-only',
    });

    if (value !== 'VISTAPRINT') {
        return false;
    }

    if (loading || !printData) {
        return false;
    }
    if (printData.printByExternalId && printData.printByExternalId.options.length > 0) {
        return true;
    }

    if (error) {
        throw error;
    }

    return false;
}

export function getValue({ value }: Props) {
    return value;
}
